import {
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Color,
  ColorMaterialProperty,
  HeightReference,
  HorizontalOrigin,
  NearFarScalar,
  PolygonHierarchy,
  PolylineDashMaterialProperty,
  PolylineGlowMaterialProperty,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "cesium";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { v4 as uuidv4 } from "uuid";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { useCesiumFeatures } from "../../../hooks/useCesiumFeatures";
import { checkIfHSLA, hslaToHex } from "../../../styles/helpers";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import Icon from "../../core/icon/Icon";
import { createIconForFeature as createSharedIconForFeature } from "./utils/cesiumIconUtils";

export interface CesiumDrawingFeatureProps {
  viewer: any;
  onDrawComplete?: (featureCollection: GeoJSON.FeatureCollection) => void;
  mapType?: {
    mode: string;
    drawType: string;
  };
  setDrawMode?: (mode: {
    mode: string | null;
    drawType: string | null;
  }) => void;
  initialDrawType?: string;
  handleSaveDrawn?: (featureCollection: GeoJSON.FeatureCollection) => void;
  featureCollection?: GeoJSON.FeatureCollection;
  shouldTriggerRefresh?: number;
  // Add support for external opacity and color updates
  onFeatureStyleUpdate?: (
    featureId: string,
    style: { color?: string; opacity?: number },
  ) => void;
  setOpenDescriptionProp?: (open: boolean) => void;
  openDescription?: boolean;
  setFeatureId?: (featureId: string | null) => void;
  featureId?: string;
  // Missing props from MapboxDrawingFeature
  allowClickMapToStartDrawing?: boolean;
  maxFeatures?: number;
  inMainMap?: boolean;
  isMobile?: boolean;
  deleteFeature?: (id: string) => void;
  descriptionProvided?: boolean | Record<string, boolean>;
  bottom?: number | string;
}

type DrawingMode = "none" | "polygon" | "path" | "area" | "point" | "mapbounds";

// GeoJSON FeatureCollection type string inline to avoid linter warnings

// Helper function to validate a single position
const isValidPosition = (position: any): boolean => {
  return (
    position &&
    typeof position.x === "number" &&
    typeof position.y === "number" &&
    typeof position.z === "number" &&
    !isNaN(position.x) &&
    !isNaN(position.y) &&
    !isNaN(position.z) &&
    isFinite(position.x) &&
    isFinite(position.y) &&
    isFinite(position.z)
  );
};

// Helper function to validate an array of positions
const validatePositionsArray = (positions: any[]): any[] => {
  return positions.filter((pos) => {
    if (!isValidPosition(pos)) {
      console.warn("Filtering out invalid position:", pos);
      return false;
    }
    return true;
  });
};

const CesiumDrawingFeature: React.FC<CesiumDrawingFeatureProps> = ({
  initialDrawType,
  onDrawComplete,
  mapType,
  setDrawMode,
  handleSaveDrawn,
  featureCollection,
  shouldTriggerRefresh,
  onFeatureStyleUpdate,
  setOpenDescriptionProp,
  openDescription,
  setFeatureId,
  featureId,
  // New props from MapboxDrawingFeature
  allowClickMapToStartDrawing = true,
  maxFeatures = 10000,
  inMainMap,
  isMobile,
  deleteFeature,
  descriptionProvided,
  bottom,
}) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<any[]>([]);
  const drawingEntityRef = useRef<any>(null);
  const drawingOutlineRef = useRef<any>(null); // For dashed polygon border
  const handlerRef = useRef<any>(null);
  const hasSavedRef = useRef<boolean>(false);
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;
  const {
    savedFeatures,
    viewer: cesiumViewer,
    addFeature,
    removeFeature,
    updateFeatureProperties,
    updateVertexPosition: updateVertexPositionHook,
    initializeSavedFeatures,
    clearSavedFeatures,
    getNextFeatureNumber,
  } = useCesiumFeatures();

  // State variables from MapboxDrawingFeature
  const [currentlyDrawing, setCurrentlyDrawing] = useState(false);
  const [continueFeat, setContinueFeat] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  // Synchronize the featureCollection prop with the global state
  useEffect(() => {
    // A deep copy is essential to prevent unintended mutations.
    const featuresAreEqual =
      JSON.stringify(featureCollection) ===
      JSON.stringify(statefulCesiumMap?.savedFeatures);

    if (featureCollection && !featuresAreEqual) {
      setStatefulCesiumMap((prev) => ({
        ...prev,
        savedFeatures: featureCollection,
      }));
    }
  }, [
    featureCollection,
    statefulCesiumMap?.savedFeatures,
    setStatefulCesiumMap,
  ]);

  const [triggerRefresh, setTriggerRefresh] = useState(false);

  // Mobile detection fallback
  const isMobileFallback = useIsMobile();
  const isMobileResolved = isMobile ?? isMobileFallback;
  const savedEntitiesRef = useRef<any[]>([]);
  const vertexEntitiesRef = useRef<any[]>([]); // Add ref for vertex entities
  const labelEntitiesRef = useRef<any[]>([]); // Add ref for label entities
  const iconEntitiesRef = useRef<any[]>([]); // Add ref for icon entities
  const positionsRef = useRef<any[]>([]); // Track current positions to avoid race conditions
  const lastClickTimeRef = useRef<number>(0); // Debounce rapid clicks
  const dragHandlerRef = useRef<any>(null); // Add ref for drag handler
  const isDraggingRef = useRef<boolean>(false);
  const dragDataRef = useRef<{
    featureId: string;
    vertexIndex: number;
    draggedEntity: any;
  } | null>(null);

  // Separate handler for click-to-start to avoid conflicts with drawing handler
  const clickToStartHandlerRef = useRef<any>(null);

  // Initialize drag state to ensure it starts clean
  const resetDragState = useCallback(() => {
    isDraggingRef.current = false;
    dragDataRef.current = null;
    const viewer = statefulCesiumMap?.viewer;
    if (viewer) {
      viewer.scene.screenSpaceCameraController.enableInputs = true;
      viewer.canvas.style.cursor = "default";
    }
  }, [statefulCesiumMap?.viewer]);

  // Handle initialDrawType changes (like Mapbox) - clear and reset drawing
  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) return;

    // Always clear state first to prevent stale state issues
    clearDrawingState(viewer);

    if (initialDrawType === "path") {
      setDrawMode?.({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode?.({ mode: "draw", drawType: "path" });
      }, 50);
    } else if (initialDrawType === "area") {
      setDrawMode?.({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode?.({ mode: "draw", drawType: "area" });
      }, 50);
    } else if (initialDrawType === "point") {
      setDrawMode?.({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode?.({ mode: "draw", drawType: "point" });
      }, 50);
    } else {
      setDrawMode?.({ mode: "draw", drawType: null });
    }
  }, [initialDrawType, statefulCesiumMap?.viewer, setDrawMode]);

  // Helper function to clear drawing state
  const clearDrawingState = useCallback((viewer: any) => {
    // Reset state
    setPositions([]);
    positionsRef.current = [];
    hasSavedRef.current = false;
    setCurrentlyDrawing(false);
    setContinueFeat(false);
    setShowMessage(false);

    // Remove drawing entities
    if (drawingEntityRef.current) {
      viewer.entities.remove(drawingEntityRef.current);
      drawingEntityRef.current = null;
    }
    if (drawingOutlineRef.current) {
      viewer.entities.remove(drawingOutlineRef.current);
      drawingOutlineRef.current = null;
    }
  }, []);

  // Manual reset function for debugging (expose to global scope)
  const forceResetDrawingState = useCallback(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) {
      return;
    }

    clearDrawingState(viewer);

    // Also restore feature selection
    if (viewer._originalFeatureSelectionHandler) {
      viewer.screenSpaceEventHandler.setInputAction(
        viewer._originalFeatureSelectionHandler,
        ScreenSpaceEventType.LEFT_CLICK,
      );
    }
  }, [statefulCesiumMap?.viewer, clearDrawingState]);

  // Expose reset function to window for debugging
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).forceResetCesiumDrawing = forceResetDrawingState;
    }
  }, [forceResetDrawingState]);

  // Function to create label for a feature
  const createLabelForFeature = useCallback(
    (feature, position, viewer) => {
      if (!feature.properties) return null;

      const currentFeatureId = feature.id || feature.properties?.id;
      const isActive = currentFeatureId === featureId;

      // Determine label text - priority: tooltipLabel > number > id
      let labelText = "";
      if (
        feature.properties.tooltipLabel &&
        feature.properties.tooltipLabel !== "none"
      ) {
        labelText = feature.properties.tooltipLabel;
      } else if (feature.properties.number) {
        labelText = feature.properties.number.toString();
      } else if (currentFeatureId) {
        labelText = currentFeatureId.toString();
      }

      // Only create label if we have text to display
      if (!labelText) return null;

      // Determine label color - matching Mapbox integration logic
      let labelColor = Color.WHITE; // Default to white

      // First priority: use labelColor if it exists
      if (feature.properties.labelColor) {
        try {
          let colorToUse = feature.properties.labelColor;
          // Convert HSLA to hex for better Cesium compatibility
          if (checkIfHSLA(colorToUse)) {
            const hexColor = hslaToHex(colorToUse);
            colorToUse = hexColor;
          }
          labelColor = Color.fromCssColorString(colorToUse);
        } catch (e) {
          // Fallback to feature color if labelColor parsing fails
          if (feature.properties.color) {
            try {
              let colorToUse = feature.properties.color;
              if (checkIfHSLA(colorToUse)) {
                colorToUse = hslaToHex(colorToUse);
              }
              labelColor = Color.fromCssColorString(colorToUse);
            } catch (e2) {
              labelColor = Color.WHITE;
            }
          }
        }
      } else if (feature.properties.color) {
        // Second priority: use feature color for any feature type
        try {
          let colorToUse = feature.properties.color;
          // Convert HSLA to hex for better Cesium compatibility
          if (checkIfHSLA(colorToUse)) {
            colorToUse = hslaToHex(colorToUse);
          }
          labelColor = Color.fromCssColorString(colorToUse);
        } catch (e) {
          labelColor = Color.WHITE;
        }
      }

      // Create label entity
      const labelEntity = viewer.entities.add({
        position: position,
        featureId: currentFeatureId + "_label",
        label: {
          text: labelText,
          font: "bold 12px Nunito, sans-serif", // Match Mapbox integration font
          pixelOffset: new Cartesian3(0, -25, 0), // Offset label above the feature
          fillColor: labelColor,
          outlineColor: Color.WHITE, // White outline/halo for readability (matching Mapbox)
          outlineWidth: 4,
          style: 2, // FILL_AND_OUTLINE
          verticalOrigin: 1, // BOTTOM
          horizontalOrigin: HorizontalOrigin.CENTER,
          heightReference: HeightReference.NONE, // Preserve 3D position
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new NearFarScalar(1.0e2, 1.0, 1.0e7, 0.5), // Scale with distance
        },
      });

      return labelEntity;
    },
    [featureId],
  );

  // Function to create icon for a feature - using shared utility
  const createIconForFeature = useCallback((feature, position, viewer) => {
    return createSharedIconForFeature({
      feature,
      position,
      viewer,
      cleanupRefs: {
        iconEntities: iconEntitiesRef,
      },
    });
  }, []);

  const prevRefreshIndexRef = useRef<number | undefined>(shouldTriggerRefresh);
  const setOpenDescription =
    setOpenDescriptionProp || (() => console.log("drawing feature"));

  useEffect(() => {
    if (!setDrawMode) return;

    if (initialDrawType === "path") {
      setDrawMode({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "path" });
      }, 10);
    } else if (initialDrawType === "area") {
      setDrawMode({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "area" });
      }, 10);
    } else if (initialDrawType === "point") {
      setDrawMode({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "point" });
      }, 10);
    } else if (initialDrawType === "mapbounds") {
      setDrawMode({ mode: null, drawType: null });
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "mapbounds" });
      }, 10);
    } else {
      setDrawMode({ mode: "draw", drawType: null });
    }
  }, [initialDrawType, setDrawMode, statefulCesiumMap?.viewer]);

  // Function to update feature geometry during dragging - optimized for real-time updates
  const updateFeatureGeometryDuringDrag = useCallback(
    (featureId: string, vertexIndex: number, newPosition: any) => {
      const viewer = statefulCesiumMap?.viewer;
      const prevFeatures = statefulCesiumMap?.savedFeatures;
      if (!viewer || !prevFeatures) return;

      // Find the feature to update
      const featureToUpdate = prevFeatures.features.find(
        (feature) =>
          feature.id === featureId || feature.properties?.id === featureId,
      );

      if (!featureToUpdate || !featureToUpdate.geometry) return;

      const geometryType = featureToUpdate.geometry.type;

      // Find the visual entity to update
      const entityToUpdate = savedEntitiesRef.current.find(
        (entity) => entity.featureId === featureId,
      );

      if (entityToUpdate) {
        try {
          // Convert new position to longitude/latitude
          const cartographic = Cartographic.fromCartesian(newPosition);
          const newCoordinate = [
            CesiumMath.toDegrees(cartographic.longitude),
            CesiumMath.toDegrees(cartographic.latitude),
            cartographic.height,
          ];

          // Update the GeoJSON coordinates temporarily for visual update
          if (geometryType === "Point") {
            // For points, directly update the position
            if (entityToUpdate.position) {
              const position = Cartesian3.fromDegrees(
                newCoordinate[0],
                newCoordinate[1],
                newCoordinate[2] || 0,
              );
              (entityToUpdate.position as any).setValue(position);
            }
            return; // No need for further processing for points
          }

          let updatedCoordinates: number[][];

          if (
            geometryType === "LineString" &&
            "coordinates" in featureToUpdate.geometry
          ) {
            updatedCoordinates = [...featureToUpdate.geometry.coordinates];
            if (vertexIndex < updatedCoordinates.length) {
              updatedCoordinates[vertexIndex] = newCoordinate;
            }
          } else if (
            geometryType === "Polygon" &&
            "coordinates" in featureToUpdate.geometry
          ) {
            updatedCoordinates = [...featureToUpdate.geometry.coordinates[0]];
            if (vertexIndex < updatedCoordinates.length) {
              updatedCoordinates[vertexIndex] = newCoordinate;
              // Update the closing point if it's the first vertex
              if (vertexIndex === 0 && updatedCoordinates.length > 1) {
                updatedCoordinates[updatedCoordinates.length - 1] =
                  newCoordinate;
              }
            }
          } else {
            return;
          }

          // Update visual entity positions using setValue for performance
          const positions = updatedCoordinates.map((coord: number[]) =>
            Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
          );

          if (geometryType === "LineString" && entityToUpdate.polyline) {
            (entityToUpdate.polyline.positions as any).setValue(positions);
          } else if (geometryType === "Polygon" && entityToUpdate.polygon) {
            (entityToUpdate.polygon.hierarchy as any).setValue(
              new PolygonHierarchy(positions),
            );
          }
        } catch (error) {
          console.error("Error updating feature geometry during drag:", error);
        }
      }
    },
    [statefulCesiumMap?.viewer, statefulCesiumMap?.savedFeatures],
  );

  // Optimized version for real-time dragging - only updates visual entities
  const updateVertexPositionOptimized = useCallback(
    (featureId: string, vertexIndex: number, newPosition: any) => {
      const viewer = statefulCesiumMap?.viewer;
      const prevFeatures = statefulCesiumMap?.savedFeatures;
      if (!viewer || !prevFeatures) return;

      // Find the feature to update
      const featureToUpdate = prevFeatures.features.find(
        (feature) =>
          feature.id === featureId || feature.properties?.id === featureId,
      );

      if (!featureToUpdate || !featureToUpdate.geometry) return;

      const geometryType = featureToUpdate.geometry.type;

      // Update the visual entities only (not the data yet - that happens on drag end)
      const entityToUpdate = savedEntitiesRef.current.find(
        (entity) => entity.featureId === featureId,
      );

      if (entityToUpdate) {
        try {
          // Convert new position to longitude/latitude
          const cartographic = Cartographic.fromCartesian(newPosition);
          const newCoordinate = [
            CesiumMath.toDegrees(cartographic.longitude),
            CesiumMath.toDegrees(cartographic.latitude),
            cartographic.height,
          ];

          // Update the GeoJSON coordinates temporarily
          const updatedGeometry = { ...featureToUpdate.geometry };
          if (
            geometryType === "LineString" &&
            "coordinates" in updatedGeometry
          ) {
            const coordinates = [...updatedGeometry.coordinates];
            if (vertexIndex < coordinates.length) {
              coordinates[vertexIndex] = newCoordinate;
              updatedGeometry.coordinates = coordinates;
            }
          } else if (
            geometryType === "Polygon" &&
            "coordinates" in updatedGeometry
          ) {
            const coordinates = [...updatedGeometry.coordinates];
            if (coordinates[0] && vertexIndex < coordinates[0].length) {
              coordinates[0][vertexIndex] = newCoordinate;
              // Update the closing point if it's the first vertex
              if (vertexIndex === 0 && coordinates[0].length > 1) {
                coordinates[0][coordinates[0].length - 1] = newCoordinate;
              }
              updatedGeometry.coordinates = coordinates;
            }
          }

          // Update visual entity positions using setValue for performance
          if (geometryType === "LineString" && entityToUpdate.polyline) {
            const positions = updatedGeometry.coordinates.map(
              (coord: number[]) =>
                Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
            );
            (entityToUpdate.polyline.positions as any).setValue(positions);
          } else if (geometryType === "Polygon" && entityToUpdate.polygon) {
            const positions = updatedGeometry.coordinates[0].map(
              (coord: number[]) =>
                Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
            );
            (entityToUpdate.polygon.hierarchy as any).setValue(
              new PolygonHierarchy(positions),
            );
          }
        } catch (error) {
          console.error("Error updating visual entity during drag:", error);
        }
      }
    },
    [statefulCesiumMap?.viewer, statefulCesiumMap?.savedFeatures],
  );

  const updateVertexPosition = useCallback(
    (featureId: string, vertexIndex: number, newPosition: any) => {
      // Convert Cartesian3 position to coordinate array
      const cartographic = Cartographic.fromCartesian(newPosition);
      const newCoordinate = [
        CesiumMath.toDegrees(cartographic.longitude),
        CesiumMath.toDegrees(cartographic.latitude),
        cartographic.height,
      ];

      // Call the hook to update the geometry. The hook will handle the state update.
      updateVertexPositionHook(featureId, vertexIndex, newCoordinate, {
        handleSaveDrawn,
      });
    },
    [updateVertexPositionHook, handleSaveDrawn],
  );

  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) return;

    // Create handlers only if they don't exist
    if (!handlerRef.current) {
      handlerRef.current = new ScreenSpaceEventHandler(viewer.scene.canvas); // For drawing
    }
    if (!dragHandlerRef.current) {
      dragHandlerRef.current = new ScreenSpaceEventHandler(viewer.scene.canvas); // For dragging
    }
    if (!clickToStartHandlerRef.current) {
      clickToStartHandlerRef.current = new ScreenSpaceEventHandler(
        viewer.scene.canvas,
      ); // For click-to-start
    }

    // Store reference to the original feature selection handler
    const originalFeatureSelectionHandler = (click: any) => {
      // Don't handle feature selection if we're currently drawing
      // Use ref instead of state to avoid stale closure
      if (positionsRef.current.length > 0) {
        return;
      }

      const pickedObject = viewer.scene.pick(click.position);
      const featureId = pickedObject?.id?.featureId;
      if (featureId && setFeatureId) {
        setFeatureId(featureId);
      }
    };

    // IMPORTANT: Don't overwrite the existing click handler!
    // The main click handler from cesium-events.ts handles object clicks
    // We only need to handle drawing-specific clicks through our own handlers

    // Set up drag handlers for vertex editing and point editing
    dragHandlerRef.current.setInputAction((event: any) => {
      const pickedObject = viewer.scene.pick(event.position);

      // Check if this is a BIM model click - if so, don't consume the event
      const isBimModelClick =
        pickedObject &&
        (pickedObject.constructor?.name === "Cesium3DTileFeature" ||
          (pickedObject.primitive &&
            pickedObject.primitive.constructor?.name === "Cesium3DTileset"));

      if (isBimModelClick) {
        // Since we have separate handlers that override the main one,
        // we need to manually process the BIM click
        const feature = pickedObject as any;
        const content = feature._content;

        if (
          content &&
          content.batchTable &&
          content.batchTable._propertyTable
        ) {
          const propertyTable = content.batchTable._propertyTable;
          const metadataTable = propertyTable._metadataTable;

          if (
            metadataTable &&
            typeof metadataTable.getProperty === "function"
          ) {
            try {
              const propertyIds = metadataTable.getPropertyIds();
              const attributes: { [key: string]: any } = {};
              const batchId = feature._batchId;

              if (propertyIds && Array.isArray(propertyIds)) {
                propertyIds.forEach((id: string) => {
                  try {
                    attributes[id] = metadataTable.getProperty(batchId, id);
                  } catch (propError) {
                    console.warn(`Failed to get property ${id}:`, propError);
                    // Continue with other properties
                  }
                });

                // Get screen coordinates for cursor positioning
                const canvas = viewer.scene.canvas;
                const rect = canvas.getBoundingClientRect();
                const cursorPosition = {
                  x: rect.left + event.position.x,
                  y: rect.top + event.position.y,
                };

                // Dispatch the event that MapBuilder is listening for
                window.dispatchEvent(
                  new CustomEvent("cesiumObjectClick", {
                    detail: { attributes, cursorPosition },
                  }),
                );
                window.dispatchEvent(
                  new CustomEvent("objectClick", {
                    detail: { attributes, cursorPosition },
                  }),
                );
              }
            } catch (metadataError) {
              console.error("Error extracting BIM attributes:", metadataError);
              // Still dispatch event even if metadata extraction failed
              const canvas = viewer.scene.canvas;
              const rect = canvas.getBoundingClientRect();
              const cursorPosition = {
                x: rect.left + event.position.x,
                y: rect.top + event.position.y,
              };

              // Dispatch with meaningful fallback attributes
              const fallbackAttributes = {
                error: "Failed to extract BIM metadata",
                batchId: feature._batchId || "unknown",
                featureType: "BIM Feature",
                clickable: true,
              };

              window.dispatchEvent(
                new CustomEvent("cesiumObjectClick", {
                  detail: {
                    attributes: fallbackAttributes,
                    cursorPosition,
                  },
                }),
              );
              window.dispatchEvent(
                new CustomEvent("objectClick", {
                  detail: {
                    attributes: fallbackAttributes,
                    cursorPosition,
                  },
                }),
              );
            }
          }
        }

        return; // Don't continue with drawing logic
      }

      // Handle empty map clicks (no object picked)
      if (!pickedObject) {
        // Get screen coordinates for cursor positioning
        const canvas = viewer.scene.canvas;
        const rect = canvas.getBoundingClientRect();
        const cursorPosition = {
          x: rect.left + event.position.x,
          y: rect.top + event.position.y,
        };

        // Dispatch empty click event that AttributeDisplay is listening for
        window.dispatchEvent(
          new CustomEvent("cesiumEmptyClick", {
            detail: { cursorPosition },
          }),
        );

        return; // Don't continue with drawing logic
      }

      if (
        pickedObject?.id?.isVertex &&
        pickedObject.id.featureId === featureId
      ) {
        // Handle vertex dragging (polygons/lines) - only if feature is already active

        isDraggingRef.current = true;
        dragDataRef.current = {
          featureId: pickedObject.id.featureId,
          vertexIndex: pickedObject.id.vertexIndex,
          draggedEntity: pickedObject.id,
        };
        viewer.scene.screenSpaceCameraController.enableInputs = false;
        viewer.canvas.style.cursor = "move";
      } else if (
        pickedObject?.id?.featureId &&
        pickedObject.id.point &&
        pickedObject.id.featureId === featureId
      ) {
        // Handle point feature dragging - only if feature is already active

        isDraggingRef.current = true;
        dragDataRef.current = {
          featureId: pickedObject.id.featureId,
          vertexIndex: 0, // Points only have one position
          draggedEntity: pickedObject.id,
        };
        viewer.scene.screenSpaceCameraController.enableInputs = false;
        viewer.canvas.style.cursor = "move";
      } else {
        // Ensure we're not dragging if we didn't pick a draggable object or it's not the active feature

        resetDragState();
      }
    }, ScreenSpaceEventType.LEFT_DOWN);

    dragHandlerRef.current.setInputAction((event: any) => {
      // Performance optimization: reduce logging during drag operations
      if (isDraggingRef.current === true && dragDataRef.current !== null) {
        // First try to pick 3D world position (includes buildings, models, terrain, etc.)
        let newPosition = viewer.scene.pickPosition(event.endPosition);

        if (!isValidPosition(newPosition)) {
          // Fallback to terrain/globe picking if pickPosition failed
          const ray = viewer.camera.getPickRay(event.endPosition);
          newPosition = viewer.scene.globe.pick(ray, viewer.scene);
        }

        if (isValidPosition(newPosition)) {
          // Update vertex position directly using setValue for performance
          (dragDataRef.current.draggedEntity.position as any).setValue(
            newPosition,
          );

          // Update only the visual geometry in real-time (no data state changes)
          updateFeatureGeometryDuringDrag(
            dragDataRef.current.featureId,
            dragDataRef.current.vertexIndex,
            newPosition,
          );

          // Cesium will automatically re-render when entities are updated
          // No need for manual requestRender() which causes excessive rendering
        }
      } else if (
        isDraggingRef.current === false &&
        dragDataRef.current === null
      ) {
        // This is expected - no dragging happening (don't log to reduce noise)
      } else {
        console.warn("MOUSE_MOVE with inconsistent drag state:", {
          isDragging: isDraggingRef.current,
          dragData: !!dragDataRef.current,
        });
        // Force reset the state
        console.warn("Force resetting drag state due to inconsistency");
        resetDragState();
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);

    // Handle drag end on LEFT_UP
    dragHandlerRef.current.setInputAction((event: any) => {
      // Check if this is a BIM model click - if so, don't consume the event
      const pickedObject = viewer.scene.pick(event.position);
      const isBimModelClick =
        pickedObject &&
        (pickedObject.constructor?.name === "Cesium3DTileFeature" ||
          (pickedObject.primitive &&
            pickedObject.primitive.constructor?.name === "Cesium3DTileset"));

      if (isBimModelClick) {
        return; // Already handled in LEFT_DOWN
      }
      if (isDraggingRef.current && dragDataRef.current) {
        // Get final position and save to data
        const finalPosition =
          dragDataRef.current.draggedEntity.position.getValue();
        updateVertexPosition(
          dragDataRef.current.featureId,
          dragDataRef.current.vertexIndex,
          finalPosition,
        );
      }

      // Always reset drag state on LEFT_UP, regardless of previous state
      resetDragState();
      viewer.scene.requestRender();
    }, ScreenSpaceEventType.LEFT_UP);

    // Add safety handlers to ensure drag state is always reset
    const mouseLeaveHandler = () => {
      if (isDraggingRef.current) {
        resetDragState();
      }
    };

    const globalMouseUpHandler = () => {
      if (isDraggingRef.current) {
        resetDragState();
      }
    };

    viewer.scene.canvas.addEventListener("mouseleave", mouseLeaveHandler);
    document.addEventListener("mouseup", globalMouseUpHandler);

    // Add native DOM event listeners as additional backup
    const nativeMouseUpHandler = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        resetDragState();
      }
    };

    // const nativeMouseDownHandler = (e: MouseEvent) => {
    //   console.log(
    //     "Native DOM mousedown event detected, current isDragging:",
    //     isDraggingRef.current,
    //   );
    // };

    document.addEventListener("mouseup", nativeMouseUpHandler);
    // document.addEventListener("mousedown", nativeMouseDownHandler);

    return () => {
      // Don't destroy handlers if we're actively drawing
      const isActivelyDrawing = positionsRef.current.length > 0;

      if (isActivelyDrawing) {
        // Still clean up event listeners but keep handlers alive
        viewer.scene.canvas.removeEventListener(
          "mouseleave",
          mouseLeaveHandler,
        );
        document.removeEventListener("mouseup", globalMouseUpHandler);
        document.removeEventListener("mouseup", nativeMouseUpHandler);
        // document.removeEventListener("mousedown", nativeMouseDownHandler);
        return;
      }
      // Only destroy handlers when not actively drawing
      if (handlerRef.current && !handlerRef.current.isDestroyed()) {
        handlerRef.current.destroy();
        handlerRef.current = null;
      }
      if (dragHandlerRef.current && !dragHandlerRef.current.isDestroyed()) {
        dragHandlerRef.current.destroy();
        dragHandlerRef.current = null;
      }
      if (
        clickToStartHandlerRef.current &&
        !clickToStartHandlerRef.current.isDestroyed()
      ) {
        clickToStartHandlerRef.current.destroy();
        clickToStartHandlerRef.current = null;
      }

      // Only skip entity cleanup if there's an active drawing
      const hasActiveDrawing =
        positionsRef.current.length > 0 || drawingEntityRef.current !== null;

      if (!hasActiveDrawing) {
        if (drawingEntityRef.current) {
          viewer.entities.remove(drawingEntityRef.current);
          drawingEntityRef.current = null;
        }
        if (drawingOutlineRef.current) {
          viewer.entities.remove(drawingOutlineRef.current);
          drawingOutlineRef.current = null;
        }
      }

      // Always clean up event listeners
      viewer.scene.canvas.removeEventListener("mouseleave", mouseLeaveHandler);
      document.removeEventListener("mouseup", globalMouseUpHandler);
      document.removeEventListener("mouseup", nativeMouseUpHandler);
      // document.removeEventListener("mousedown", nativeMouseDownHandler);
    };
  }, [
    statefulCesiumMap?.viewer,
    setFeatureId,
    updateFeatureGeometryDuringDrag,
    updateVertexPosition,
    resetDragState,
    featureId,
  ]);

  const handleSave = useCallback(() => {
    const viewer = statefulCesiumMap?.viewer;
    const drawType = mapType?.drawType;
    const isPolygon =
      drawType === "polygon" || drawType === "area" || drawType === "mapbounds";
    const isPoint = drawType === "point";
    const currentFeatureId = featureId || uuidv4(); // Should already have featureId from startDrawing

    // Get positions from the entity instead of the state
    let currentPositions: any[] = [];

    if (drawingEntityRef.current) {
      if (isPolygon && drawingEntityRef.current.polygon) {
        currentPositions =
          drawingEntityRef.current.polygon.hierarchy.getValue().positions;
      } else if (drawingEntityRef.current.polyline) {
        currentPositions =
          drawingEntityRef.current.polyline.positions.getValue();
      } else if (isPoint && drawingEntityRef.current.position) {
        currentPositions = [drawingEntityRef.current.position.getValue()];
      }
    }
    // if (positions?.length > 0) {
    //   currentPositions = positions;
    // }

    // Don't save if already saved this drawing
    if (hasSavedRef.current) {
      return;
    }

    // Don't save if there's no drawing entity
    if (!drawingEntityRef.current || currentPositions.length === 0) {
      return;
    }

    // const hasEnoughPoints = isPoint
    //   ? currentPositions.length >= 1
    //   : isPolygon
    //     ? currentPositions.length >= 3
    //     : currentPositions.length >= 2;

    // if (!hasEnoughPoints) {
    //   alert(
    //     t("Need more points: ") +
    //       (isPolygon
    //         ? "3 for polygon"
    //         : isPoint
    //           ? "1 for point"
    //           : "2 for line"),
    //   );
    //   return;
    // }

    // Validate all positions before creating geometry
    const validatedPositions = validatePositionsArray(currentPositions);
    if (validatedPositions.length !== currentPositions.length) {
      console.warn("Some positions were invalid and filtered out", {
        original: currentPositions.length,
        valid: validatedPositions.length,
      });
      currentPositions = validatedPositions;

      // Re-check if we still have enough points after filtering
      const stillHasEnoughPoints = isPoint
        ? currentPositions.length >= 1
        : isPolygon
          ? currentPositions.length >= 3
          : currentPositions.length >= 2;

      if (!stillHasEnoughPoints) {
        console.error("Not enough valid positions after filtering");
        return;
      }
    }

    // For points, we need to make sure we have exactly one point
    if (isPoint && currentPositions.length > 1) {
      currentPositions = [currentPositions[0]];
    }

    const coordinates = currentPositions.map((position) => {
      const cartographic = Cartographic.fromCartesian(position);
      return [
        CesiumMath.toDegrees(cartographic.longitude),
        CesiumMath.toDegrees(cartographic.latitude),
        cartographic.height, // Preserve height information
      ];
    });

    // For polygons, close the shape by adding the first point at the end
    if (isPolygon && coordinates.length > 0) {
      coordinates.push(coordinates[0]);
    }

    // Calculate the next number based on existing features
    const nextNumber = getNextFeatureNumber();

    // Set default color
    const defaultColor = "#CD992E";

    // Create proper geometry based on type
    let geometry: GeoJSON.Geometry;
    if (isPoint) {
      geometry = {
        type: "Point",
        coordinates: coordinates[0],
      };
      if (isMobile) {
        setOpenDescription?.(true);
        setFeatureId?.(currentFeatureId);
      }
    } else if (drawType === "path") {
      geometry = {
        type: "LineString",
        coordinates,
      };
    } else {
      geometry = {
        type: "Polygon",
        coordinates: [coordinates],
      };
    }

    // Create a GeoJSON feature
    const feature: GeoJSON.Feature = {
      type: "Feature",
      id: currentFeatureId,
      properties: {
        id: currentFeatureId,
        drawType: drawType,
        description: "",
        choices: null,
        color: defaultColor,
        user_color: defaultColor,
        number: nextNumber,
        opacity: drawType === "point" ? 1.0 : drawType === "path" ? 1.0 : 0.4,
        tooltipLabel: nextNumber.toString(), // Default label is the number
      },
      geometry: geometry,
    };

    // Use the streamlined feature management
    addFeature(feature, { handleSaveDrawn, onDrawComplete });

    // Clean up after saving using centralized function
    if (viewer) {
      clearDrawingState(viewer);
      hasSavedRef.current = true; // Mark as saved
    }

    // Don't remove handlers - they will be properly managed by the useEffect cleanup
    // Removing them here can cause issues with rapid clicking and continuous drawing
    // The handlers will be recreated with fresh closures when needed by the useEffect

    // Reset drawing state after save
    setCurrentlyDrawing(false);
    setContinueFeat(false);

    // Restore the original feature selection handler
    if (viewer && viewer._originalFeatureSelectionHandler) {
      viewer.screenSpaceEventHandler.setInputAction(
        viewer._originalFeatureSelectionHandler,
        ScreenSpaceEventType.LEFT_CLICK,
      );
    }

    // Note: For point mode, drawing will be restarted by the useEffect that watches mapType
  }, [
    statefulCesiumMap?.viewer,
    mapType?.drawType,
    statefulCesiumMap?.savedFeatures,
    handleSaveDrawn,
    onDrawComplete,
    setStatefulCesiumMap,
    featureId,
    descriptionProvided,
    setOpenDescription,
  ]);

  const startDrawing = useCallback(
    (mode: DrawingMode, firstClickPosition?: any) => {
      const viewer = statefulCesiumMap?.viewer;
      if (!viewer || !handlerRef.current) {
        return;
      }

      // Reset positions and entities but keep currentlyDrawing state

      setPositions([]);
      positionsRef.current = [];
      lastClickTimeRef.current = 0; // Reset click debounce timer
      hasSavedRef.current = false; // Reset the flag for new drawing

      // Generate and set featureId immediately when starting to draw
      const newFeatureId = uuidv4();
      setFeatureId?.(newFeatureId);

      // Remove any existing drawing entities
      if (drawingEntityRef.current) {
        viewer.entities.remove(drawingEntityRef.current);
        drawingEntityRef.current = null;
      }
      if (drawingOutlineRef.current) {
        viewer.entities.remove(drawingOutlineRef.current);
        drawingOutlineRef.current = null;
      }

      // Temporarily disable the main feature selection handler while drawing
      viewer.screenSpaceEventHandler.removeInputAction(
        ScreenSpaceEventType.LEFT_CLICK,
      );

      // Remove any existing drawing entity
      if (drawingEntityRef.current) {
        viewer.entities.remove(drawingEntityRef.current);
      }
      if (drawingOutlineRef.current) {
        viewer.entities.remove(drawingOutlineRef.current);
      }

      // Convert area to polygon mode
      const effectiveMode =
        mode === "area" || mode === "mapbounds" ? "polygon" : mode;

      if (effectiveMode === "polygon") {
        // Create a new entity for the polygon (fill only, no outline)
        drawingEntityRef.current = viewer.entities.add({
          polygon: {
            hierarchy: new PolygonHierarchy(),
            material: Color.fromCssColorString("#CD992E").withAlpha(0.4), // Default color with 0.4 opacity
            outline: false, // No outline on polygon itself
            perPositionHeight: true,
          },
        });

        // Create a separate polyline entity for the dashed border
        drawingOutlineRef.current = viewer.entities.add({
          polyline: {
            positions: [],
            width: 2,
            material: new PolylineDashMaterialProperty({
              color: Color.BLACK,
              dashLength: 16.0,
            }),
            // Remove clampToGround to follow terrain heights from coordinates
          },
        });
      } else if (effectiveMode === "path") {
        // Create a new entity for the line
        drawingEntityRef.current = viewer.entities.add({
          polyline: {
            positions: [],
            width: 2,
            material: new PolylineDashMaterialProperty({
              color: Color.BLACK,
              dashLength: 16.0,
            }),
            clampToGround: true,
          },
        });
      } else if (effectiveMode === "point") {
        // Create a new entity for the point
        drawingEntityRef.current = viewer.entities.add({
          point: {
            pixelSize: 10,
            color: Color.RED,
            outlineColor: Color.WHITE,
            outlineWidth: 2,
            heightReference: HeightReference.NONE, // Preserve 3D position from pickPosition
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
      }

      // Handle left click to add points - this should run with highest priority

      // Create handler if it doesn't exist or was destroyed
      if (!handlerRef.current || handlerRef.current.isDestroyed()) {
        handlerRef.current = new ScreenSpaceEventHandler(viewer.scene.canvas);
      }

      // Remove any existing handler first to ensure clean state
      handlerRef.current.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
      handlerRef.current.setInputAction((movement: any) => {
        let cartesian: any;

        // Check what we're clicking on for logging purposes
        const pickedObject = viewer.scene.pick(movement.position);

        // First, always try pickPosition - this will work for any 3D object including underground models
        cartesian = viewer.scene.pickPosition(movement.position);

        // Only fallback to terrain/globe if pickPosition truly failed (returned undefined or invalid)
        if (!cartesian || !isValidPosition(cartesian)) {
          if (pickedObject) {
            // There's something there but pickPosition failed, try with depth test disabled
            const depthTestBackup = viewer.scene.globe.depthTestAgainstTerrain;
            viewer.scene.globe.depthTestAgainstTerrain = false;
            cartesian = viewer.scene.pickPosition(movement.position);
            viewer.scene.globe.depthTestAgainstTerrain = depthTestBackup;
          }

          // If still no valid position, use terrain picking
          if (!isValidPosition(cartesian)) {
            const ray = viewer.camera.getPickRay(movement.position);
            cartesian = viewer.scene.globe.pick(ray, viewer.scene);
          }

          // Final fallback to ellipsoid if terrain picking also failed
          if (!isValidPosition(cartesian)) {
            cartesian = viewer.camera.pickEllipsoid(
              movement.position,
              viewer.scene.globe.ellipsoid,
            );
          }
        }

        if (isValidPosition(cartesian)) {
          // Check if handler is still valid
          if (!handlerRef.current || handlerRef.current.isDestroyed()) {
            console.error("Drawing handler is destroyed! Cannot add vertex.");
            return;
          }

          // Debounce rapid clicks with a longer delay to prevent issues
          const now = Date.now();
          const timeSinceLastClick = now - lastClickTimeRef.current;

          if (timeSinceLastClick < 100) {
            return;
          }
          lastClickTimeRef.current = now;

          hasSavedRef.current = false; // Mark as having unsaved changes

          // Use ref to avoid race conditions with rapid clicks
          const newPositions = [...positionsRef.current, cartesian];

          positionsRef.current = newPositions;
          setPositions(newPositions);

          // Update the entity based on drawing mode - check entity exists first
          if (effectiveMode === "polygon") {
            if (drawingEntityRef.current && drawingEntityRef.current.polygon) {
              drawingEntityRef.current.polygon.hierarchy = new PolygonHierarchy(
                newPositions,
              );
              // Update the dashed border polyline
              if (
                drawingOutlineRef.current &&
                drawingOutlineRef.current.polyline &&
                newPositions.length > 1
              ) {
                // Close the polygon for the border by adding the first point at the end
                const closedPositions = [...newPositions];
                if (newPositions.length > 2) {
                  closedPositions.push(newPositions[0]);
                }
                drawingOutlineRef.current.polyline.positions = closedPositions;
              }
            } else {
              console.warn(
                "Drawing entity for polygon not ready, skipping update",
              );
            }
          } else if (effectiveMode === "path") {
            if (drawingEntityRef.current && drawingEntityRef.current.polyline) {
              drawingEntityRef.current.polyline.positions = newPositions;
            } else {
              console.warn(
                "Drawing entity for path not ready, skipping update",
              );
            }
          } else if (effectiveMode === "point") {
            if (drawingEntityRef.current) {
              drawingEntityRef.current.position = cartesian;
              // For points, we want to complete the drawing immediately
              setTimeout(() => {
                if (newPositions.length > 0) {
                  handleSave();
                }
              }, 0);
            } else {
              console.warn(
                "Drawing entity for point not ready, skipping update",
              );
            }
          }
        }
      }, ScreenSpaceEventType.LEFT_CLICK);

      // If we have a first click position, process it immediately as the first vertex
      if (firstClickPosition) {
        // Convert screen position to world position using the same logic as regular clicks
        let cartesian: any;

        // Check what we're clicking on
        const pickedObject = viewer.scene.pick(firstClickPosition);

        // First, always try pickPosition - this will work for any 3D object including underground models
        cartesian = viewer.scene.pickPosition(firstClickPosition);

        // Only fallback to terrain/globe if pickPosition truly failed (returned undefined or invalid)
        if (!cartesian || !isValidPosition(cartesian)) {
          if (pickedObject) {
            // There's something there but pickPosition failed, try with depth test disabled
            const depthTestBackup = viewer.scene.globe.depthTestAgainstTerrain;
            viewer.scene.globe.depthTestAgainstTerrain = false;
            cartesian = viewer.scene.pickPosition(firstClickPosition);
            viewer.scene.globe.depthTestAgainstTerrain = depthTestBackup;
          }

          // If still no valid position, use terrain picking
          if (!isValidPosition(cartesian)) {
            const ray = viewer.camera.getPickRay(firstClickPosition);
            cartesian = viewer.scene.globe.pick(ray, viewer.scene);
          }

          // Final fallback to ellipsoid if terrain picking also failed
          if (!isValidPosition(cartesian)) {
            cartesian = viewer.camera.pickEllipsoid(
              firstClickPosition,
              viewer.scene.globe.ellipsoid,
            );
          }
        }

        if (isValidPosition(cartesian)) {
          // Add the first position
          const newPositions = [cartesian];
          positionsRef.current = newPositions;
          setPositions(newPositions);

          // Set the initial click time to prevent immediate subsequent clicks from being ignored
          lastClickTimeRef.current = Date.now();

          // Update the entity based on drawing mode - with safety checks
          if (effectiveMode === "polygon") {
            if (drawingEntityRef.current && drawingEntityRef.current.polygon) {
              drawingEntityRef.current.polygon.hierarchy = new PolygonHierarchy(
                newPositions,
              );
            }
          } else if (effectiveMode === "path") {
            if (drawingEntityRef.current && drawingEntityRef.current.polyline) {
              drawingEntityRef.current.polyline.positions = newPositions;
            }
          } else if (effectiveMode === "point") {
            if (drawingEntityRef.current) {
              drawingEntityRef.current.position = cartesian;
              // For points, complete drawing immediately
              setTimeout(() => {
                handleSave();
              }, 0);
            }
          }
        } else {
        }
      }

      // Add right-click handler to complete drawing
    },
    [statefulCesiumMap?.viewer, handleSave],
  );

  // Handle click-to-start drawing when allowClickMapToStartDrawing is true (interactive mode)
  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;

    if (!viewer || !clickToStartHandlerRef.current || mapType?.mode !== "draw")
      return;

    let clickHandler: any;

    if (!currentlyDrawing) {
      clickHandler = (event: any) => {
        // Check if we're at max features limit
        const currentFeatureCount = featureCollection?.features?.length || 0;
        if (currentFeatureCount >= maxFeatures) {
          setShowMessage(true);
          setTimeout(() => setShowMessage(false), 3000);
          return;
        }

        // Only start drawing if not currently drawing and description is not open
        // On desktop, ignore openDescription if setOpenDescriptionProp is not provided
        const shouldBlockDrawing =
          openDescription && (isMobileResolved || setOpenDescriptionProp);

        if (!shouldBlockDrawing && mapType?.drawType && !currentlyDrawing) {
          // Check if clicked on an existing drawing feature (not just any 3D object)
          const pickedObject = viewer.scene.pick(event.position);
          const isExistingDrawingFeature =
            pickedObject?.id?.featureId &&
            (pickedObject.id.featureId === featureId || // Currently selected feature
              savedEntitiesRef.current.some(
                (entity) => entity.featureId === pickedObject.id.featureId,
              ) || // Saved drawing features
              vertexEntitiesRef.current.some(
                (entity) => entity.featureId === pickedObject.id.featureId,
              )); // Vertex dots

          if (!isExistingDrawingFeature) {
            setCurrentlyDrawing(true);

            // Start drawing and capture the first click position
            startDrawing(mapType?.drawType as DrawingMode, event.position);
          } else {
          }
        } else {
        }
      };

      // Set up click handler on separate handler
      clickToStartHandlerRef.current.setInputAction(
        clickHandler,
        ScreenSpaceEventType.LEFT_CLICK,
      );
    }

    return () => {
      if (
        clickHandler &&
        clickToStartHandlerRef.current &&
        !clickToStartHandlerRef.current.isDestroyed()
      ) {
        clickToStartHandlerRef.current.removeInputAction(
          ScreenSpaceEventType.LEFT_CLICK,
        );
      }
    };
  }, [
    mapType?.mode,
    mapType?.drawType,
    allowClickMapToStartDrawing,
    currentlyDrawing,
    openDescription,
    startDrawing,
  ]);

  const handleSaveAll = (id: string) => {
    handleSave();

    // Use descriptionProvided prop which is managed by the parent component
    // This avoids issues with async state updates
    const hasDescription =
      typeof descriptionProvided === "object"
        ? descriptionProvided[id]
        : descriptionProvided;

    if (hasDescription) {
      setOpenDescription?.(false);
      setFeatureId?.(null);
    } else {
      setOpenDescription?.(true);
    }

    // Reset drawing mode briefly to hide buttons, then restore drawing mode
    setDrawMode?.({ mode: "none", drawType: null });
    // setFeatureId?.(null);
    if (!isMobile || !setOpenDescriptionProp) {
      setFeatureId?.(null);
    }

    // Ensure drawing state is fully cleared before restoring drawing mode
    const viewer = statefulCesiumMap?.viewer;
    if (viewer) {
      clearDrawingState(viewer);
    }

    // Restore drawing mode after a short delay
    setTimeout(() => {
      if (mapType?.drawType) {
        if (allowClickMapToStartDrawing) {
          // When click-to-start is enabled, auto-restart drawing mode after save (continuous drawing)
          setDrawMode?.({ mode: "draw", drawType: mapType.drawType });
          // Don't call startDrawing() - user will click to start the next feature
        } else {
          // When click-to-start is disabled, only start drawing when explicitly set via setDrawMode
          // Don't auto-restart drawing mode
        }
      }
    }, 150); // Slightly longer delay to ensure state is fully reset
  };

  const handleDelete = (id: string) => {
    setFeatureId?.(null);

    // Always reset drawing state after deletion using centralized function
    const viewer = statefulCesiumMap?.viewer;
    if (viewer) {
      clearDrawingState(viewer);
    }

    // Use the streamlined feature management
    removeFeature(id, { handleSaveDrawn });

    // Restore drawing mode after deletion based on allowClickMapToStartDrawing
    if (mapType?.drawType) {
      setTimeout(() => {
        if (allowClickMapToStartDrawing) {
          // When click-to-start is enabled, restore drawing mode for continuous drawing
          setDrawMode?.({ mode: "draw", drawType: mapType.drawType });
        } else {
          // When click-to-start is disabled, only restore if we're not already in draw mode
          if (mapType?.mode !== "draw") {
            setDrawMode?.({ mode: "draw", drawType: mapType.drawType });
          }
        }
      }, 100);
    }
  };

  // Utility function to update feature style (color and opacity)
  const updateFeatureStyle = useCallback(
    (featureId: string, style: { color?: string; opacity?: number }) => {
      // Build the properties to update
      const propertiesToUpdate: Record<string, unknown> = {};
      if (style.color) {
        propertiesToUpdate.color = style.color;
        propertiesToUpdate.user_color = style.color;
      }
      if (style.opacity !== undefined) {
        propertiesToUpdate.opacity = style.opacity;
      }

      // Use the streamlined feature management
      updateFeatureProperties(featureId, propertiesToUpdate, {
        handleSaveDrawn,
      });

      // Call the external style update callback if provided
      if (onFeatureStyleUpdate) {
        onFeatureStyleUpdate(featureId, style);
      }
    },
    [handleSaveDrawn, onFeatureStyleUpdate, updateFeatureProperties],
  );

  // Expose the updateFeatureStyle function through multiple methods
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).updateCesiumFeatureStyle = updateFeatureStyle;
    }
  }, [updateFeatureStyle]);

  // Check if we're in drawing mode
  const isDrawingMode = mapType?.mode === "draw";

  // Add a new useEffect to display saved features with robust styling
  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) {
      return;
    }

    // Use either the prop or global state
    const featuresToDisplay = statefulCesiumMap?.savedFeatures;

    // Always clear existing entities first
    savedEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    savedEntitiesRef.current = [];

    vertexEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    vertexEntitiesRef.current = [];

    labelEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    labelEntitiesRef.current = [];

    iconEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    iconEntitiesRef.current = [];

    // If no features to display, we're done (entities are already cleared)
    if (
      !featuresToDisplay ||
      !featuresToDisplay.features ||
      featuresToDisplay.features.length === 0
    ) {
      return;
    }

    // Display each saved feature with robust styling
    featuresToDisplay.features.forEach((feature) => {
      if (!feature.geometry) {
        return;
      }

      const geometryType = feature.geometry.type;

      // Convert GeoJSON coordinates to Cesium positions
      let positions: any[] = [];

      try {
        if (geometryType === "Point" && "coordinates" in feature.geometry) {
          const coordinates = feature.geometry.coordinates;
          positions = [
            Cartesian3.fromDegrees(
              coordinates[0],
              coordinates[1],
              coordinates[2] || 0,
            ),
          ];
        } else if (
          geometryType === "LineString" &&
          "coordinates" in feature.geometry
        ) {
          const coordinates = feature.geometry.coordinates;
          positions = coordinates.map((coord: number[]) =>
            Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
          );
        } else if (
          geometryType === "Polygon" &&
          "coordinates" in feature.geometry
        ) {
          const coordinates = feature.geometry.coordinates;
          positions = coordinates[0].map((coord: number[]) =>
            Cartesian3.fromDegrees(coord[0], coord[1], coord[2] || 0),
          );
        } else {
          return;
        }
      } catch (error) {
        console.error("Error processing coordinates for feature:", error);
        return;
      }

      // Create the appropriate entity based on geometry type with robust styling
      let entity: any;
      try {
        if (geometryType === "Point") {
          // Only show the point dot if there's no icon defined
          const hasIcon = feature.properties?.icon;

          if (!hasIcon) {
            // Use color from properties if available, otherwise use default
            const baseColor = feature.properties?.color
              ? Color.fromCssColorString(feature.properties.color)
              : Color.BLACK;

            // Get opacity from properties if available, otherwise use default
            let opacity = 1.0; // Default opacity for points
            if (feature.properties?.opacity !== undefined) {
              const parsedOpacity = Number.parseFloat(
                feature.properties.opacity,
              );
              if (
                !isNaN(parsedOpacity) &&
                parsedOpacity >= 0 &&
                parsedOpacity <= 1
              ) {
                opacity = parsedOpacity;
              }
            }

            // Create a color with the specified opacity
            const pointColor = Color.fromAlpha(baseColor, opacity);
            const currentFeatureId = feature.id || feature.properties?.id;
            const isActive = currentFeatureId === featureId;

            entity = viewer.entities.add({
              position: positions[0],
              featureId: currentFeatureId, // Store feature ID for picking
              point: {
                pixelSize: isActive ? 15 : 10, // Larger when active
                color: isActive ? Color.WHITE.withAlpha(opacity) : pointColor,
                outlineColor: isActive ? Color.BLACK : Color.WHITE,
                outlineWidth: isActive ? 3 : 2, // Thicker outline when active
                heightReference: HeightReference.NONE, // Preserve absolute 3D position
                disableDepthTestDistance: Number.POSITIVE_INFINITY, // Ensure visibility
              },
            });
          } else {
            // Create an invisible entity for picking purposes when icon is present
            const currentFeatureId = feature.id || feature.properties?.id;
            entity = viewer.entities.add({
              position: positions[0],
              featureId: currentFeatureId, // Store feature ID for picking
              point: {
                pixelSize: 1,
                color: Color.TRANSPARENT, // Invisible point for click detection
                show: false, // Hide the point completely
              },
            });
          }
        } else if (geometryType === "LineString") {
          // Use color from properties if available, otherwise use default
          const baseColor = feature.properties?.color
            ? Color.fromCssColorString(feature.properties.color)
            : Color.BLACK;

          // Get opacity from properties if available, otherwise use default
          let opacity = 1.0; // Default opacity for lines
          if (feature.properties?.opacity !== undefined) {
            const parsedOpacity = Number.parseFloat(feature.properties.opacity);
            if (
              !isNaN(parsedOpacity) &&
              parsedOpacity >= 0 &&
              parsedOpacity <= 1
            ) {
              opacity = parsedOpacity;
            }
          }

          // Create a color with the specified opacity
          const lineColor = Color.fromAlpha(baseColor, opacity);
          const currentFeatureId = feature.id || feature.properties?.id;
          const isActive = currentFeatureId === featureId;

          entity = viewer.entities.add({
            featureId: currentFeatureId, // Store feature ID for picking
            polyline: {
              positions: positions,
              width: 2, // Consistent 2px width
              material: isActive
                ? new PolylineDashMaterialProperty({
                    color: Color.BLACK,
                    dashLength: 16.0,
                  })
                : lineColor,
              clampToGround: true,
            },
          });
        } else if (geometryType === "Polygon") {
          // Get base color from properties if available, otherwise use default
          const baseColor = feature.properties?.color
            ? Color.fromCssColorString(feature.properties.color)
            : Color.BLACK;

          // Get opacity from properties if available, otherwise use default
          let opacity = 0.4; // Default opacity for polygons
          if (feature.properties?.opacity !== undefined) {
            const parsedOpacity = Number.parseFloat(feature.properties.opacity);
            if (
              !isNaN(parsedOpacity) &&
              parsedOpacity >= 0 &&
              parsedOpacity <= 1
            ) {
              opacity = parsedOpacity;
            }
          }

          // Create a color with the specified opacity using fromAlpha for consistency
          const fillColor = Color.fromAlpha(baseColor, opacity);
          const currentFeatureId = feature.id || feature.properties?.id;
          const isActive = currentFeatureId === featureId;

          // Use regular entity approach for consistent opacity handling
          const outlineColor = feature.properties?.outlineColor
            ? Color.fromCssColorString(feature.properties.outlineColor)
            : baseColor; // Use same color as fill

          // Create polygon without outline
          entity = viewer.entities.add({
            featureId: currentFeatureId, // Store feature ID for picking
            polygon: {
              hierarchy: new PolygonHierarchy(positions),
              material: new ColorMaterialProperty(fillColor), // Keep original fill color
              outline: false, // No outline on polygon itself
              perPositionHeight: true,
            },
          });

          // Create separate polyline for the border
          const borderPositions = [...positions, positions[0]]; // Close the polygon
          const borderEntity = viewer.entities.add({
            featureId: currentFeatureId + "_border", // Store feature ID for picking
            polyline: {
              positions: borderPositions,
              width: 2,
              material: isActive
                ? new PolylineDashMaterialProperty({
                    color: Color.BLACK,
                    dashLength: 16.0,
                  })
                : Color.fromAlpha(outlineColor, 1.0),
              // Remove clampToGround to follow terrain heights from coordinates
            },
          });

          // Store both entities for cleanup
          savedEntitiesRef.current.push(borderEntity);
        }
      } catch (error) {
        console.error("Error creating entity for feature:", error);
        return;
      }

      if (entity) {
        savedEntitiesRef.current.push(entity);
      }

      // Create label for the feature
      try {
        let labelPosition: any;
        const currentFeatureId = feature.id || feature.properties?.id;

        if (geometryType === "Point") {
          // For points, use the point position
          labelPosition = positions[0];
        } else if (geometryType === "LineString") {
          // For lines, use the middle point
          const middleIndex = Math.floor(positions.length / 2);
          labelPosition = positions[middleIndex];
        } else if (geometryType === "Polygon") {
          // For polygons, calculate centroid
          let totalX = 0;
          let totalY = 0;
          let totalZ = 0;
          positions.forEach((pos) => {
            totalX += pos.x;
            totalY += pos.y;
            totalZ += pos.z;
          });
          labelPosition = new Cartesian3(
            totalX / positions.length,
            totalY / positions.length,
            totalZ / positions.length,
          );
        }

        if (labelPosition) {
          const labelEntity = createLabelForFeature(
            feature,
            labelPosition,
            viewer,
          );
          if (labelEntity) {
            labelEntitiesRef.current.push(labelEntity);
          }
        }

        // Create icon for the feature if it has an icon property
        if (feature.properties?.icon) {
          const iconEntities = createIconForFeature(
            feature,
            labelPosition,
            viewer,
          );
          if (iconEntities) {
            iconEntitiesRef.current.push(iconEntities.backgroundEntity);
            // Note: iconEntity is initially null due to delay, but will be created by setTimeout
            // The delayed entity will be cleaned up when the viewer is destroyed
          }
        }
      } catch (error) {
        console.error("Error creating label/icon for feature:", error);
      }

      // Add vertex dots for active LineString and Polygon features
      const currentFeatureId = feature.id || feature.properties?.id;
      const isActive = currentFeatureId === featureId;

      if (
        isActive &&
        (geometryType === "LineString" || geometryType === "Polygon")
      ) {
        try {
          positions.forEach((position, index) => {
            const vertexEntity = viewer.entities.add({
              position: position,
              // Add metadata for drag functionality
              isVertex: true,
              featureId: currentFeatureId,
              vertexIndex: index,
              point: {
                pixelSize: 12,
                color: Color.WHITE,
                outlineColor: Color.BLACK,
                outlineWidth: 2,
                heightReference: HeightReference.NONE, // Preserve 3D position
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
              },
            });

            if (vertexEntity) {
              // Store additional metadata on the entity for tracking
              vertexEntity.featureId = currentFeatureId;
              vertexEntity.vertexIndex = index;
              vertexEntitiesRef.current.push(vertexEntity);
            }
          });
        } catch (error) {
          console.error("Error creating vertex dots for feature:", error);
        }
      }
    });
  }, [
    statefulCesiumMap?.savedFeatures,
    statefulCesiumMap?.viewer,
    featureId,
    createLabelForFeature,
    createIconForFeature,
  ]);

  // Add an effect to initialize savedFeatures from featureCollection when component mounts
  useEffect(() => {
    if (featureCollection) {
      initializeSavedFeatures(featureCollection);
    }
  }, [featureCollection, initializeSavedFeatures]);

  // Replace the shouldTriggerRefresh useEffect with this one
  useEffect(() => {
    // Only trigger when shouldTriggerRefresh changes
    if (
      shouldTriggerRefresh !== undefined &&
      shouldTriggerRefresh !== prevRefreshIndexRef.current
    ) {
      // Reset drag state first to ensure clean state
      resetDragState();

      // Clear saved features using streamlined management
      clearSavedFeatures();

      // Clear all entities from the map
      const viewer = statefulCesiumMap?.viewer;
      if (viewer) {
        savedEntitiesRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        savedEntitiesRef.current = [];

        vertexEntitiesRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        vertexEntitiesRef.current = [];

        labelEntitiesRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        labelEntitiesRef.current = [];

        iconEntitiesRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        iconEntitiesRef.current = [];
      }

      // Reset drawing mode
      // setDrawMode?.({ mode: "none", drawType: null });

      // Reset positions only if no active drawing
      setPositions([]);
      positionsRef.current = [];

      // Clean up any active drawing only if no active drawing
      if (drawingEntityRef.current && viewer) {
        viewer.entities.remove(drawingEntityRef.current);
        drawingEntityRef.current = null;
      }

      if (drawingOutlineRef.current && viewer) {
        viewer.entities.remove(drawingOutlineRef.current);
        drawingOutlineRef.current = null;
      }

      // if (handlerRef.current) {
      //   handlerRef.current.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
      //   handlerRef.current.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      // }
    }

    // Update the ref for the next comparison
    prevRefreshIndexRef.current = shouldTriggerRefresh;
  }, [
    shouldTriggerRefresh,
    statefulCesiumMap?.viewer,
    clearSavedFeatures,
    resetDragState,
  ]);

  return (
    <div className="z-[9999999999999] flex flex-col gap-2 h-[45px]">
      {/* Max features message (like MapboxDrawingFeature) */}
      {ReactDOM.createPortal(
        showMessage && !openDescription && (
          <div
            className={cn(
              !isMobileResolved &&
                "fixed top-[var(--h-progress-bar,0px)] start-0 w-[calc(var(--w-screen,100vw)/2)] p-3",
              isMobileResolved && "fixed bottom-20 border-t",
            )}
          >
            <div
              className={cn(
                "relative w-full h-auto flex pointer-events-all gap-2.5 transition-colors duration-200 bg-surface-default p-2.5 -order-1",
                isMobileResolved
                  ? "ms-auto border-b border-border-subdued"
                  : "rounded-lg border shadow-lg",
              )}
            >
              <Icon name="alert" className=" text-icon-critical shrink-0" />
              <span>
                {t(
                  `map:drawInfoTooltip.maxFeaturesReached.${mapType?.drawType || "point"}`,
                )}
              </span>
            </div>
          </div>
        ),
        document.getElementById("survey-footer-container") || document.body,
      )}

      {featureId && (
        <div
          className={cn(
            "flex justify-center z-[1]",
            isMobileResolved
              ? "fixed left-0 w-screen bottom-0"
              : "absolute left-1/2 -translate-x-1/2 w-max bottom-6",
          )}
        >
          <div
            className={cn(
              "w-full h-auto flex flex-col pointer-events-all md:rounded-3xl gap-2.5 p-5 z-[999999999] transition-colors duration-200",
            )}
          >
            <div
              className={cn(
                "flex gap-2",
                isMobileResolved ? "ml-[50px] w-[calc(100%-50px)]" : "w-full",
              )}
            >
              {/* <div className="w-full flex justify-end">
                <Button
                  size="lg"
                  text={t("util:delete", {
                    val: t(
                      `global:${
                        mapType?.drawType === "point"
                          ? "point"
                          : mapType?.drawType === "path"
                            ? "path"
                            : "area"
                      }`,
                    ),
                  })}
                  width="max"
                  onClick={() => {
                    handleDelete(featureId || "");
                  }}
                />
              </div>
              <Button
                variant="primary"
                leadingIcon="checkmark-md"
                text={isMobileResolved ? "" : t("global:save")}
                size="lg"
                disabled={openDescription === true && !(typeof descriptionProvided === 'object' ? descriptionProvided[featureId] : descriptionProvided)}
                disabledNotice={t("map:drawInfoTooltip.descriptionMissing")}
                dataCy="drawingFeatureSave"
                onClick={() => {
                  handleSetAddDescription(featureId, "save");
                  handleSaveAll(featureId);
                }}
              /> */}

              <Button
                size="lg"
                variant="white"
                leadingIcon={
                  isMobileResolved && continueFeat ? "trash" : undefined
                }
                text={
                  isMobileResolved && continueFeat
                    ? ""
                    : t("util:delete", {
                        val: t(
                          `global:${
                            mapType?.drawType === "point"
                              ? "point"
                              : mapType?.drawType === "path"
                                ? "path"
                                : "area"
                          }`,
                        ),
                      })
                }
                onClick={() => {
                  if (featureId) {
                    handleDelete(featureId);
                  }
                }}
              />
              <Button
                variant="primary"
                leadingIcon="checkmark-md"
                text={isMobileResolved && continueFeat ? "" : t("global:save")}
                size="lg"
                disabled={
                  openDescription === true &&
                  !(typeof descriptionProvided === "object"
                    ? descriptionProvided[featureId]
                    : descriptionProvided)
                }
                disabledNotice={t("map:drawInfoTooltip.descriptionMissing")}
                dataCy="drawingFeatureSave"
                onClick={() => {
                  handleSaveAll(featureId || "");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CesiumDrawingFeature;
